import { Button, Col, Row, Space, Typography } from 'antd'
import { Link } from 'react-router-dom'

import { Mascot } from './mascot'

/**
 * The hero.
 *
 * The headline sells the outcome rather than the technique, which is what the
 * brief asked for and what most developer tools get backwards. "Continuous
 * profiling for Go" describes the method and puts us on Pyroscope's and
 * Datadog's ground; "know which deploy made production slower" describes the
 * thing somebody actually wants at four in the afternoon.
 *
 * The call to action says trial rather than free, because there is no free
 * plan. A visitor who clicks "free" and finds a price list has been told
 * something untrue by the first thing they read, and the refund policy —
 * which rests entirely on the trial being real — reads very differently after
 * that.
 */

/**
 * The five lines, and they are the five lines that actually work.
 *
 * Version is included because without it deploy comparison cannot run: there is
 * no boundary between builds, the detector returns immediately, and the
 * customer gets a product that silently never alerts. Two of this operator's
 * own services shipped that way for fifteen hundred profiles before anybody
 * noticed. A homepage example that omits it teaches every new customer to make
 * the same mistake.
 */
const SNIPPET = `import "github.com/peccancy/pecgoprof-go"

profiler.Start(profiler.Config{
    APIKey:      os.Getenv("PECGOPROF_API_KEY"),
    Endpoint:    "https://pecgoprof.online",
    Service:     "checkout-api",
    Environment: "production",
    Version:     os.Getenv("GIT_SHA"),
})`

export function Hero() {
  return (
    <div
      style={{
        background: 'linear-gradient(160deg, #0b0f14 0%, #131c28 100%)',
        color: '#fff',
        padding: '72px 24px 84px',
      }}
    >
      <div style={{ maxWidth: 1120, margin: '0 auto' }}>
        <Row gutter={[48, 40]} align="middle">
          <Col xs={24} lg={12}>
            {/* Floated rather than stacked: above the headline it was a band of
                its own with the sentence starting underneath, which reads as
                two unrelated things. Floated, the headline begins beside it and
                closes underneath, and the two are one block. */}
            <div style={{ float: 'left', marginRight: 16, marginTop: 2, lineHeight: 0 }}>
              <Mascot pose="hero" size={100} />
            </div>
            <Typography.Title
              style={{
                color: '#fff',
                fontSize: 'clamp(32px, 5vw, 52px)',
                lineHeight: 1.1,
                marginTop: 0,
              }}
            >
              Know which deploy made your <span style={{ color: '#4096ff' }}>Go</span> service
              slower
            </Typography.Title>

            <Typography.Paragraph
              style={{ color: 'rgba(255,255,255,0.72)', fontSize: 18, maxWidth: 520 }}
            >
              PecGoProf profiles your Go services continuously, compares every build with the one
              it replaced, and tells you which function started costing more — before you go
              looking for it.
            </Typography.Paragraph>

            <Space size={12} wrap style={{ marginTop: 8, marginBottom: 20 }}>
              <Link to="/login?tab=register&plan=developer">
                <Button type="primary" size="large">
                  Start your 14-day trial
                </Button>
              </Link>
              <Link to="/demo">
                <Button size="large" ghost>
                  See a live demo
                </Button>
              </Link>
              <Link to="/pricing">
                <Typography.Text style={{ color: 'rgba(255,255,255,0.7)' }}>Pricing</Typography.Text>
              </Link>
            </Space>

            <div>
              <Typography.Text style={{ color: 'rgba(255,255,255,0.5)', fontSize: 13 }}>
                14 days, no card required · Plans from €14.99 a month · Go 1.22 and newer
              </Typography.Text>
            </div>
          </Col>

          <Col xs={24} lg={12}>
            <div
              style={{
                background: '#0d1218',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: 12,
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  padding: '10px 16px',
                  borderBottom: '1px solid rgba(255,255,255,0.08)',
                  color: 'rgba(255,255,255,0.45)',
                  fontSize: 12,
                }}
              >
                main.go — the whole integration
              </div>
              <pre
                style={{
                  margin: 0,
                  padding: 20,
                  overflowX: 'auto',
                  color: '#d6e4ff',
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                  fontSize: 13.5,
                  lineHeight: 1.65,
                }}
              >
                {SNIPPET}
              </pre>
            </div>
            <Typography.Text
              style={{ color: 'rgba(255,255,255,0.45)', fontSize: 12.5, display: 'block', marginTop: 10 }}
            >
              Version is what makes deploy comparison possible. Without it the profiles still
              arrive, but there is no boundary between builds to compare across.
            </Typography.Text>
          </Col>
        </Row>
      </div>
    </div>
  )
}
